import { FC } from "react";

import {
  Box,
  Column,
  Row,
  UpsellButton,
  UpsellHeading,
  IconButton,
  CloseIcon,
  UpgradeIcon,
  RefreshIcon,
  AudienceIcon,
  LockIcon,
  LightningBoltIcon,
  ChakraModalOverlay,
  ChakraModalContent,
  ChakraModalBody,
  ChakraModal,
  Text,
  ButtonGroup,
  Heading,
  HtImage,
  HtVideo,
} from "@hightouch/htui";

import { Link, LinkButton } from "src/router";

import * as analytics from "src/lib/analytics";
import { Logo } from "src/ui/brand";

import { FeatureProps } from "./types";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  featureName: string;
  featureDetails?: FeatureProps;
};

const UPSELL_PITCH = [
  {
    name: "Supercharged syncing",
    description:
      "Run your syncs at sub-hourly frequencies, send data to premium destinations, and more.",
    icon: RefreshIcon,
  },
  {
    name: "Enterprise security",
    description:
      "Access our enterprise features including SSO, RBAC, audit logs, and custom storage.",
    icon: LockIcon,
  },
  {
    name: "Streamlined workflows",
    description:
      "Integrate Hightouch with Git, turn on approval flows, and plug into tools like Fivetran.",
    icon: LightningBoltIcon,
  },
  {
    name: "Premium add-on products",
    description:
      "Transform your warehouse into a Customer Data Platform (CDP) with a suite of no-code features.",
    icon: AudienceIcon,
  },
];

export const UpsellModal: FC<Readonly<ModalProps>> = ({
  featureName,
  featureDetails,
  isOpen,
  onClose,
}) => {
  const { pitch, description, bullets, video, image } = featureDetails || {};

  const trackUnlockClick = () => {
    analytics.track("Business Tier Upgrade Clicked");
  };

  const trackLearnMoreClick = () => {
    analytics.track("Business Tier Learn More Clicked");
  };

  return (
    <ChakraModal
      isCentered
      scrollBehavior="inside"
      isOpen={isOpen}
      size={featureDetails ? "5xl" : "3xl"}
      onClose={onClose}
    >
      <ChakraModalOverlay />
      <ChakraModalContent p={0} maxHeight="85%" my="auto">
        <ChakraModalBody
          p={0}
          my={0}
          overflowY="auto"
          borderRadius="2xl"
          boxShadow="md"
        >
          <Box position="absolute" right={4} top={4}>
            <IconButton
              aria-label="Close modal"
              icon={CloseIcon}
              onClick={onClose}
            />
          </Box>
          <Row
            display="grid"
            gridTemplateColumns={featureDetails ? "3fr 4fr" : "1fr"}
          >
            {featureDetails && (
              <Column
                alignItems="start"
                bg="base.background"
                gap={6}
                justifyContent="center"
                p={8}
              >
                <Column gap={2}>
                  <Heading size="lg">{pitch}</Heading>
                  <Text color="text.secondary" mt={1}>
                    {description}
                  </Text>
                  <Column as="ul" pl={6} gap={2}>
                    {bullets?.map((bullet) => (
                      <Box key={bullet} as="li">
                        <Text color="text.secondary">{bullet}</Text>
                      </Box>
                    ))}
                  </Column>
                </Column>
                {video && (
                  <HtVideo
                    poster={video.poster}
                    src={video.src}
                    borderRadius="lg"
                    boxShadow="buttonShadowLight"
                    overflow="hidden"
                  />
                )}
                {image && (
                  <HtImage
                    src={image.src}
                    alt={image.alt}
                    borderRadius="lg"
                    boxShadow="buttonShadowLight"
                    overflow="hidden"
                  />
                )}
              </Column>
            )}
            <Column gap={6} justifyContent="space-between" p={8}>
              <Column gap={6}>
                <Column gap={2}>
                  <Logo theme="dark" fill="var(--chakra-colors-upsell-base)" />

                  <UpsellHeading size="2xl">
                    {featureDetails
                      ? "Upgrade your plan to Business Tier"
                      : `Upgrade to Business Tier to get ${featureName}`}
                  </UpsellHeading>
                  <Text color="text.secondary">
                    Unlock the full power of Hightouch with our Business Tier
                    plan. Activate your data with enhanced security, governance,
                    developer tools, and premium destinations.
                  </Text>
                </Column>
                <Column gap={6}>
                  <Box display="grid" gap={4} gridTemplateColumns="1fr 1fr">
                    {UPSELL_PITCH.map((feature, idx) => (
                      <Row key={idx} align="start" gap={2}>
                        <Column mt={1}>
                          <Row as={feature.icon} boxSize={4} flexShrink={0} />
                        </Column>
                        <Column gap={2}>
                          <Text size="lg" fontWeight="semibold">
                            {feature.name}
                          </Text>
                          <Text size="sm" color="text.secondary">
                            {feature.description}
                          </Text>
                        </Column>
                      </Row>
                    ))}
                  </Box>
                  <Text fontWeight="medium">
                    There's more to Business Tier.{" "}
                    <Link
                      isExternal
                      href="https://hightouch.com/pricing"
                      variant="upsell"
                    >
                      See all the features and compare plans
                    </Link>
                    .
                  </Text>
                </Column>
              </Column>

              <Column gap={2}>
                <ButtonGroup>
                  <LinkButton
                    isJustified
                    target="_blank"
                    href="https://hightouch.com/pricing"
                    size="lg"
                    onClick={trackLearnMoreClick}
                  >
                    Learn more
                  </LinkButton>
                  <UpsellButton
                    isExternal
                    isJustified
                    href="https://hightouch.com/join/business-tier-upgrade"
                    size="lg"
                    icon={UpgradeIcon}
                    onClick={trackUnlockClick}
                  >
                    Request to upgrade
                  </UpsellButton>
                </ButtonGroup>
                <Text size="sm" color="text.tertiary" fontWeight="medium">
                  Our team will ask you about your needs, help you understand
                  how pricing works, and get you started with new features.
                </Text>
              </Column>
            </Column>
          </Row>
        </ChakraModalBody>
      </ChakraModalContent>
    </ChakraModal>
  );
};
